/**
 * Gap B wave 1a: inline Pause / Enable on every row of the Management
 * drill-down (campaign, ad group, ad, keyword).
 *
 * Art's ask (agreed 2026-09-23): the action sits in the row you are
 * looking at, one click, no modal, no pop-up.
 *
 * The flow for one click:
 *  1. The row goes busy; further clicks do nothing until it settles.
 *  2. The existing write for that level is sent (no new API client).
 *  3. The row's own list is re-read from the server and the row's status
 *     is taken from that read, not from the write's response. Staging runs
 *     dry-run, where the write "succeeds" and nothing changes; the re-read
 *     is what tells the two apart.
 *  4. A toast that stays until closed says what happened, with Undo. Undo
 *     sends the opposite write through the same path, but only after a
 *     fresh read shows the item is still where this change left it. A
 *     toast can sit open for an hour; by then someone may have moved the
 *     item in Google Ads, and a blind undo would overwrite them.
 *  5. Undo clicked while the same row is still saving waits for that save,
 *     then runs (and its fresh read still decides whether to write).
 *  6. Undo toasts belong to the client they were made for. They are closed
 *     when the selected client changes or the drill-down unmounts, so an
 *     Undo can never act after a switch.
 *
 * Whether a level may write comes from the server's capabilities object
 * (write_capabilities.py) on the ad-monitor read the drill-down already
 * makes. A withheld level shows the control disabled with a plain reason
 * on hover, never hidden.
 */
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useQueryClient, type QueryKey } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { updateLiveCampaignStatus } from '@/services/googleAdsCampaignsService';
import {
  setAdGroupStatus,
  setAdStatus,
  setKeywordStatus,
  type GaWriteCapabilities,
} from '@/services/googleAdsManagementService';
import { stickyToast } from '@/features/ads/stickyToast';

export type DrilldownLevel = 'campaign' | 'ad_group' | 'ad' | 'keyword';
type WritableStatus = 'ENABLED' | 'PAUSED';

/** What the bulk control routes return. Campaign writes return nothing we
 *  count, so they map to null and the re-read decides. */
interface BulkWriteResult {
  changed: number;
  failed?: string[];
  skipped?: string[];
  capped?: boolean;
  drifted?: string[];
}

interface LevelConfig {
  capability: keyof GaWriteCapabilities;
  /** Plural noun for sentences like "Pausing and enabling ad groups ...". */
  plural: string;
  /** Field on the level's list read that holds the rows. */
  listField: 'campaigns' | 'ad_groups' | 'ads' | 'keywords';
  write: (
    tenantId: string,
    resourceName: string,
    status: WritableStatus,
    undoFrom: WritableStatus | undefined,
  ) => Promise<BulkWriteResult | null>;
}

export const LEVELS: Record<DrilldownLevel, LevelConfig> = {
  campaign: {
    capability: 'campaign_ad_status_write',
    plural: 'campaigns',
    listField: 'campaigns',
    write: async (tenantId, resourceName, status) => {
      await updateLiveCampaignStatus({
        tenantId,
        campaignResourceName: resourceName,
        status,
      });
      return null;
    },
  },
  ad_group: {
    capability: 'ad_group_write',
    plural: 'ad groups',
    listField: 'ad_groups',
    write: (tenantId, resourceName, status) =>
      setAdGroupStatus(tenantId, [resourceName], status),
  },
  ad: {
    capability: 'ad_status_write',
    plural: 'ads',
    listField: 'ads',
    // Only Undo passes the status it expects to find, so the server can
    // refuse an undo on an ad that somebody moved in Google Ads meanwhile.
    write: (tenantId, resourceName, status, undoFrom) =>
      setAdStatus(tenantId, [resourceName], status, undoFrom),
  },
  keyword: {
    capability: 'keyword_write',
    plural: 'keywords',
    listField: 'keywords',
    write: (tenantId, resourceName, status) =>
      setKeywordStatus(tenantId, [resourceName], status),
  },
};

// ---------------------------------------------------------------------------
// Write access, shared by every row in one drill-down
// ---------------------------------------------------------------------------

export interface DrilldownWriteAccess {
  tenantId: string | null;
  capabilities: GaWriteCapabilities | undefined;
  /** State of the read that carries the capabilities object. */
  capabilitiesState: 'loading' | 'error' | 'ready';
}

export const DrilldownWriteContext = createContext<DrilldownWriteAccess>({
  tenantId: null,
  capabilities: undefined,
  capabilitiesState: 'loading',
});

/** Why the control is off, or null when it may write. */
export function blockedReason(
  access: DrilldownWriteAccess,
  level: DrilldownLevel,
): string | null {
  const { plural, capability } = LEVELS[level];
  if (!access.tenantId) return 'Pick a workspace first.';
  if (access.capabilitiesState === 'loading') {
    return 'Checking whether changes are allowed on this account.';
  }
  // A missing object is a read problem, not a "no" from the server. Say so
  // instead of reading like a policy.
  if (access.capabilitiesState === 'error' || !access.capabilities) {
    return 'Core could not check whether changes are allowed here. Reload the page, or make the change in Google Ads.';
  }
  if (access.capabilities[capability] !== true) {
    return `Pausing and enabling ${plural} is switched off for this account. Make the change in Google Ads instead.`;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Plain-word errors
// ---------------------------------------------------------------------------

interface ApiErrorShape {
  response?: { status?: number; data?: { detail?: unknown } };
  message?: string;
}

/** Turns a failed write into what went wrong and what to do. Never shows the
 *  server's own text: it is written for engineers, not for this screen. */
export function describeStatusWriteError(err: unknown, plural: string): string {
  const e = (err ?? {}) as ApiErrorShape;
  const status = e.response?.status;
  const detail = e.response?.data?.detail;
  const detailText = typeof detail === 'string' ? detail.toLowerCase() : '';
  const detailObj =
    detail && typeof detail === 'object'
      ? (detail as { error?: unknown })
      : null;

  if (!e.response) {
    return 'Core could not be reached. Check your connection, then try again.';
  }
  if (status === 503 || detailObj?.error === 'feature_disabled') {
    return `Pausing and enabling ${plural} is switched off for this account right now. Make the change in Google Ads instead.`;
  }
  if (status === 401)
    return 'Your session ended. Sign in again, then try again.';
  if (status === 403) {
    return 'Your role on this workspace cannot pause or enable. Ask a workspace admin for editor access.';
  }
  if (detailText.includes('not connected')) {
    return 'Google Ads is not connected for this account. Reconnect it in Settings, under Integrations, then try again.';
  }
  if (
    detailText.includes('customer_id') ||
    detailText.includes('does not belong')
  ) {
    return 'This workspace is not linked to the Google Ads account that owns this item. Check the Google Ads connection in Settings, under Integrations.';
  }
  if (typeof status === 'number' && status >= 500) {
    return 'Google Ads did not accept the change. Try again in a minute; if it keeps failing, make the change in Google Ads.';
  }
  return 'Try again in a minute, or make the change in Google Ads.';
}

/** Why a bulk route reported zero changes for our one item. */
function describeNoChange(result: BulkWriteResult): string {
  if (result.drifted && result.drifted.length > 0) {
    return 'It was changed in Google Ads since this page loaded, so Core left it alone. Refresh to see where it is now.';
  }
  if (result.capped || (result.skipped && result.skipped.length > 0)) {
    return 'Core has a limit on how many can be paused at once on this account, and it has been reached. Try again later or make the change in Google Ads.';
  }
  return 'Google Ads did not accept the change. Try again in a minute, or make the change in Google Ads.';
}

function statusWord(status: string): string {
  const s = status.toUpperCase();
  if (s === 'ENABLED') return 'enabled';
  if (s === 'PAUSED') return 'paused';
  return 'not enabled or paused';
}

// ---------------------------------------------------------------------------
// The per-row hook
// ---------------------------------------------------------------------------

export interface StatusToggleOptions {
  level: DrilldownLevel;
  resourceName: string;
  /** Shown in the toast: "Paused <name>." */
  name: string;
  status: string | null | undefined;
  /** The react-query key of the list this row came from; re-read after a write. */
  rereadKey: QueryKey;
  /** False for rows that exist only in Core (not live in Google Ads). */
  live?: boolean;
}

export interface StatusToggle {
  busy: boolean;
  /** null for removed or unknown-status rows: they get no control. */
  control: ReactNode;
}

type RereadOutcome =
  { kind: 'status'; status: string } | { kind: 'missing' } | { kind: 'failed' };

// ---------------------------------------------------------------------------
// Open Undo toasts, by the client they were made for
// ---------------------------------------------------------------------------

type ToastId = string | number;
const openUndoToasts = new Map<ToastId, string>();

/** Close every open Undo toast. The drill-down calls this when the selected
 *  client changes and when it unmounts. */
export function dismissAllUndoToasts(): void {
  for (const id of openUndoToasts.keys()) toast.dismiss(id);
  openUndoToasts.clear();
}

// ---------------------------------------------------------------------------
// The per-row hook
// ---------------------------------------------------------------------------

export function useDrilldownStatusToggle({
  level,
  resourceName,
  name,
  status,
  rereadKey,
  live = true,
}: StatusToggleOptions): StatusToggle {
  const access = useContext(DrilldownWriteContext);
  const queryClient = useQueryClient();
  const reasonId = useId();
  const [busy, setBusy] = useState(false);
  // The save in progress on this row, if any. A ref, not state: the second
  // click of a double click must see the first straight away.
  const inFlight = useRef<Promise<void> | null>(null);
  const config = LEVELS[level];

  const reread = useCallback(async (): Promise<RereadOutcome> => {
    try {
      await queryClient.refetchQueries(
        { queryKey: rereadKey, exact: true },
        { throwOnError: true },
      );
    } catch {
      return { kind: 'failed' };
    }
    const data = queryClient.getQueryData<Record<string, unknown>>(rereadKey);
    const rows = (data?.[config.listField] ?? []) as Array<{
      resource_name?: string;
      status?: string | null;
    }>;
    const row = rows.find((r) => r.resource_name === resourceName);
    if (!row) return { kind: 'missing' };
    return { kind: 'status', status: (row.status ?? '').toUpperCase() };
  }, [queryClient, rereadKey, config.listField, resourceName]);

  /**
   * One write, start to finish. `undoOf` is set only for Undo: the status
   * this row's earlier change left it in, which a fresh read must still
   * show before anything is written.
   */
  // Plain functions, not useCallback: they only share the inFlight ref and
  // setBusy, both stable, and an Undo toast keeps whichever copy made it.
  async function perform(
    tenantId: string,
    target: WritableStatus,
    undoOf?: WritableStatus,
  ): Promise<void> {
    const verb = target === 'PAUSED' ? 'pause' : 'enable';

    if (undoOf) {
      const before = await reread();
      if (before.kind === 'failed') {
        stickyToast.error(
          `Did not undo the change to ${name}. Core could not check where it is now in Google Ads, so nothing was changed. Refresh and try again.`,
        );
        return;
      }
      if (before.kind === 'missing') {
        stickyToast.error(
          `Did not undo the change to ${name}. It no longer shows in this list, so nothing was changed.`,
        );
        return;
      }
      if (before.status !== undoOf) {
        stickyToast.error(
          `Did not undo the change to ${name}. It was changed since (it is now ${statusWord(before.status)}), so nothing was done.`,
        );
        return;
      }
    }

    let result: BulkWriteResult | null;
    try {
      result = await config.write(tenantId, resourceName, target, undoOf);
    } catch (err) {
      stickyToast.error(
        `Could not ${verb} ${name}. ${describeStatusWriteError(err, config.plural)}`,
      );
      return;
    }
    if (result && result.changed < 1) {
      stickyToast.error(`Did not ${verb} ${name}. ${describeNoChange(result)}`);
      // The row may still be stale; refresh it so it shows the truth.
      await reread();
      return;
    }

    const after = await reread();
    if (after.kind === 'failed') {
      stickyToast.warning(
        `Sent the change for ${name}, but Core could not read it back from Google Ads. Refresh the page to check its status.`,
      );
      return;
    }
    if (after.kind === 'missing') {
      stickyToast.warning(
        `Sent the change for ${name}, but it no longer shows in this list. Refresh the page to check it in Google Ads.`,
      );
      return;
    }
    if (after.status !== target) {
      stickyToast.warning(
        `Google Ads still shows ${name} as ${statusWord(after.status)}. The change can take a minute to show; refresh to check again. If it stays the same, this account may be in test mode.`,
      );
      return;
    }

    const opposite: WritableStatus = target === 'PAUSED' ? 'ENABLED' : 'PAUSED';
    const id: ToastId = stickyToast.success(
      `${target === 'PAUSED' ? 'Paused' : 'Enabled'} ${name}.`,
      {
        action: {
          label: 'Undo',
          onClick: () => {
            // Closed by a client switch but still on screen for a frame:
            // do nothing.
            if (openUndoToasts.get(id) !== tenantId) return;
            openUndoToasts.delete(id);
            void undo(tenantId, opposite, target);
          },
        },
        onDismiss: () => {
          openUndoToasts.delete(id);
        },
      },
    );
    openUndoToasts.set(id, tenantId);
  }

  function start(
    tenantId: string,
    target: WritableStatus,
    undoOf?: WritableStatus,
  ): Promise<void> {
    const job = perform(tenantId, target, undoOf).finally(() => {
      inFlight.current = null;
      setBusy(false);
    });
    inFlight.current = job;
    setBusy(true);
    return job;
  }

  /** Undo waits for any save still running on this row, then runs. */
  async function undo(
    tenantId: string,
    target: WritableStatus,
    undoOf: WritableStatus,
  ): Promise<void> {
    while (inFlight.current) {
      await inFlight.current;
    }
    await start(tenantId, target, undoOf);
  }

  const click = (target: WritableStatus) => {
    // Second click of a double click, or a click mid-save: ignore.
    if (inFlight.current) return;
    if (!access.tenantId) return;
    void start(access.tenantId, target);
  };

  const current = (status ?? '').toUpperCase();
  if (!live || (current !== 'ENABLED' && current !== 'PAUSED')) {
    return { busy, control: null };
  }

  const target: WritableStatus = current === 'ENABLED' ? 'PAUSED' : 'ENABLED';
  const reason = blockedReason(access, level);
  const label = target === 'PAUSED' ? 'Pause' : 'Enable';
  const busyLabel = target === 'PAUSED' ? 'Pausing' : 'Enabling';

  const control = (
    // The title sits on a wrapper: a disabled button takes no pointer
    // events, so its own title would never show on hover. Screen readers
    // get the same reason through aria-describedby.
    <span
      title={reason ?? `${label} ${name} in Google Ads`}
      className="inline-flex"
      data-testid={`status-toggle-${level}`}
    >
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={busy || reason !== null}
        aria-busy={busy || undefined}
        aria-label={`${label} ${name}`}
        aria-describedby={reason ? reasonId : undefined}
        onClick={() => click(target)}
        className="h-6 gap-1 px-2 text-[11px]"
      >
        {busy ? (
          <>
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
            {busyLabel}
          </>
        ) : (
          label
        )}
      </Button>
      {reason ? (
        <span id={reasonId} className="sr-only">
          {reason}
        </span>
      ) : null}
    </span>
  );
  return { busy, control };
}

/** Classes for a row that is mid-write. */
export const BUSY_ROW_CLASS = 'opacity-60';
